import { AxiosError } from 'axios';
import { useState } from 'react';
import { MutationState } from '../interfaces/entity-ui-state';

interface UseMutationProps<P, D> {
  mutationTarget$: (params?: P) => Promise<D>;
  onMutationSuccess?: (response: D) => void;
  onMutationError?: (error: AxiosError) => void;
}

interface UseMutationReturnProps<P, D> {
  mutationState: MutationState;
  error?: AxiosError;
  mutate: (params?: P) => void;
  response?: D;
}

export function useMutation<P, D>({
  mutationTarget$,
  onMutationSuccess,
  onMutationError
}: UseMutationProps<P, D>): UseMutationReturnProps<P, D> {
  const [{ mutationState, error, response }, setState] = useState<{
    mutationState: MutationState;
    error?: AxiosError;
    response?: D;
  }>({
    mutationState: MutationState.IDLE
  });

  const mutate = (params?: P) => {
    if (mutationState === MutationState.MUTATING) {
      return;
    }

    setState({
      mutationState: MutationState.MUTATING,
      response: undefined,
      error: undefined
    });

    mutationTarget$(params).then(
      res => {
        setState({
          mutationState: MutationState.IDLE,
          response: res
        });

        if (typeof onMutationSuccess === 'function') {
          onMutationSuccess(res);
        }
      },
      err => {
        setState({
          mutationState: MutationState.ERRORED,
          error: err
        });

        if (typeof onMutationError === 'function') {
          onMutationError(err);
        }
      }
    );
  };

  return {
    mutationState,
    error,
    response,
    mutate
  };
}
